<template>
  <tr
    class="participants-courses-item-row"
    :class="course.done ? 'active' : ''"
  >
    <td class="participants-courses-item-col">
      <div class="d-flex align-center">
        <v-avatar
          width="56"
          height="56"
          rounded
          :class="
            `participants-courses-item__thumbnail mr-3 ${
              hasFile ? 'participants-courses-item__thumbnail--selected' : ''
            }`
          "
        >
          <v-img
            :src="thumbnail"
            :lazy-src="require('@/assets/placeholder_other.png')"
            :alt="course.title"
            :key="
              `course-step-list-item-thumbnail-${course.id}-${thumbnailKey}`
            "
            @error="onThumbnailError"
            @click="showPreview"
          >
            <template v-slot:placeholder>
              <div class="d-flex align-center fill-height justify-center">
                <v-progress-circular
                  indeterminate
                  color="accent"
                ></v-progress-circular>
              </div>
            </template>
          </v-img>
        </v-avatar>
        <a
          class="participants-courses-item__name"
          @click="$emit('to-course', course.id)"
        >
          <h4 class="participants-courses-item__title">
            {{ course.title }}
          </h4>
          <p
            class="participants-courses-item__description"
            v-if="course.description"
          >
            {{ course.description }}
          </p>
        </a>
      </div>
    </td>
    <td class="participants-courses-item-col">
      <span class="text-nowrap">
        <div class="d-flex align-center">
          <rating-stars
            v-if="course.enrolled"
            color="#945EFF"
            :rating="course.rating"
            :size="12"
          />&nbsp;<strong>{{ course.enrolled ? course.rating : '-' }}</strong>
        </div>
      </span>
    </td>
    <td class="participants-courses-item-col">
      <span class="bhGray2--text text-nowrap">
        {{ courseStarted }}
      </span>
    </td>
    <td class="participants-courses-item-col">
      <span class="text-nowrap">
        {{ courseSteps }}
      </span>
    </td>
    <td class="participants-courses-item-col">
      <div class="d-flex align-center">
        <dot-status
          :status="course"
          :enrolled="course.enrolled"
          :completePercentage="course.completePercentage"
        />
      </div>
    </td>
    <td class="participants-courses-item-col">
      <div class="d-flex no-wrap justify-end">
        <v-btn
          text
          color="accent"
          elevation="0"
          rounded
          class="mr-2"
          @click="$emit('unassign', course.id)"
        >
          Unassign
        </v-btn>
        <v-btn
          icon
          color="accent"
          elevation="0"
          @click="$emit('to-course', course.id)"
        >
          <v-icon>mdi-arrow-right</v-icon>
        </v-btn>
      </div>
    </td>
  </tr>
</template>

<script>
import RatingStars from '@/components/exercises/RatingStars.vue'
import DotStatus from '@/components/statuses/DotStatus.vue'
import { debounce } from '@/helpers/debounce'
import globalPreviewMediaMixin from '@/mixins/globalPreviewMedia'
import { getFileTypeByUrl } from '@/helpers/chat'
import moment from 'moment'

export default {
  name: 'ParticipantCoursesTableRow',
  mixins: [globalPreviewMediaMixin],
  components: {
    RatingStars,
    DotStatus,
  },
  props: {
    user: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      isThumbnailError: false,
      thumbnailKey: 0,
    }
  },
  methods: {
    onThumbnailError() {
      if (this.thumbnailKey >= 3) {
        this.isThumbnailError = true
        return
      }
      debounce(
        () => {
          this.thumbnailKey++
        },
        5000,
        `REFRESH_COURSE_THUMBNAIL_${this.user?.id}`,
      )
    },
    showPreview() {
      if (this.hasFile) {
        this.openGlobalPreviewMedia({
          mediaUrl:
            this.user?.staticMediaThumbnailUrl ||
            this.user?.mediaUrl ||
            this.user?.thumbnailUrl,
          imageName: null,
          mlResourceUrl: null,
          fileTypeId: null,
        })
      }
    },
  },
  computed: {
    course() {
      return this.user
    },
    courseStarted() {
      return this.course.enrolled
        ? moment(this.course.enrolledDate).format('MMMM Do, YYYY')
        : '-'
    },
    courseSteps() {
      return this.course.enrolled
        ? `${this.course.stepsCompleted} / ${this.course.steps}`
        : '-'
    },
    thumbnail() {
      if (this.isThumbnailError) {
        const { type } = getFileTypeByUrl(
          this.user?.thumbnailUrl || this.user?.mediaUrl,
        )
        if (type === 'image') {
          return require('@/assets/placeholder_image.png')
        }
        if (type === 'video') {
          return require('@/assets/placeholder_video.png')
        }
        if (type === 'pdf') {
          return require('@/assets/placeholder_pdf.png')
        }
        return require('@/assets/placeholder_other.png')
      }
      return (
        this.user?.staticMediaThumbnailUrl ||
        this.user?.thumbnailUrl ||
        this.user?.mediaUrl ||
        require('@/assets/placeholder_other.png')
      )
    },
    hasFile() {
      return !!(
        this.user?.staticMediaThumbnailUrl ||
        this.user?.thumbnailUrl ||
        this.user?.mediaUrl
      )
    },
  },
}
</script>

<style lang="scss">
.participants-courses-item {
  &-row {
    border-radius: $border-radius-root * 2;
    background: transparent !important;
    overflow: hidden;

    &.active {
      cursor: pointer;
      .participants-courses-item-col {
        background: $bh-white;
      }
    }
  }

  &-col {
    padding: 8px !important;
    border: 0px solid #000;
    border-color: $bh-gray--5 !important;
    border-top-width: 1px;
    border-bottom-width: 1px;
    height: auto !important;

    strong {
      display: inline-block;
      margin-bottom: 4px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    p {
      font-size: 0.857rem;
      line-height: 1rem;
    }

    p:last-child {
      margin-bottom: 0;
    }

    &:first-of-type {
      min-width: 0;
      border-left-width: 1px;
      border-top-left-radius: $border-radius-root * 2;
      border-bottom-left-radius: $border-radius-root * 2;
    }

    &:last-of-type {
      min-width: 0;
      border-right-width: 1px;
      border-top-right-radius: $border-radius-root * 2;
      border-bottom-right-radius: $border-radius-root * 2;
      text-align: right;
    }
    .v-chip {
      text-transform: capitalize;
      font-weight: 500;
    }
    .text-number {
      font-size: 1.125rem;
    }
  }

  &__thumbnail--selected {
    cursor: pointer;
  }

  &__title {
    margin-bottom: 0;
    color: #000;
    font-size: 16px;
    line-height: 24px;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    max-width: 350px;
  }

  &__description {
    color: rgba(0, 0, 0, 0.6);
    font-size: 12px;
    line-height: 16px;
    margin-bottom: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    line-clamp: 3;
    -webkit-box-orient: vertical;
    white-space: normal;
  }
}
</style>
